// src/components/ProtectedRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import api from 'services/api/config/api';
import { useAuth } from 'contexts/AuthContext';
import { useLogging } from 'contexts/LoggingContext';
import { UserRoleType } from 'types/userRoles';
import LoadingSpinner from 'components/LoadingSpinner';
import { redirectToOnboarding, shouldRedirectToOnboarding } from 'utils/tenantStatusGate';

const ProtectedRouteLoadingSpinner = () => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
        }}
    >
        <LoadingSpinner />
    </div>
);

const ProtectedRoute = ({ requiredRoles = [] }) => {
    const [localUserRoles, setLocalUserRoles] = useState<UserRoleType[]>([]);
    const [loading, setLoading] = useState(true);
    const [redirectingToOnboarding, setRedirectingToOnboarding] = useState(false);
    const { setUserRoles, setIsAuthenticated, isAuthenticated } = useAuth();
    const { info, warn, error } = useLogging();
    const location = useLocation();

    useEffect(() => {
        const verifyAuth = async () => {
            const startTime = Date.now();

            try {
                setLoading(true);
                const response = await api.get('/auth/verify');

                // Extract roles from response
                const roles = response.data.user || [];
                const normalizedRoles = (Array.isArray(roles) ? roles : [roles]) as UserRoleType[];
                const tenantStatus = response.data.tenantStatus as string | undefined;

                if (shouldRedirectToOnboarding(tenantStatus, normalizedRoles)) {
                    setRedirectingToOnboarding(true);
                    redirectToOnboarding();
                    return;
                }

                // Update state in this component
                setIsAuthenticated(true);
                setLocalUserRoles(normalizedRoles);

                // Update AuthContext with roles
                setUserRoles(normalizedRoles);
            } catch (authError: any) {
                const verificationTime = Date.now() - startTime;

                // Log authentication failure
                error(
                    'Authentication verification failed',
                    authError,
                    {
                        path: location.pathname,
                        verificationTime,
                        errorStatus: authError.response?.status,
                        errorMessage: authError.response?.data?.message || authError.message,
                        timestamp: new Date().toISOString(),
                    },
                    'ProtectedRoute',
                    'auth_verification_failed'
                );

                setIsAuthenticated(false);
                setLocalUserRoles([]);
                setUserRoles([]);
            } finally {
                setLoading(false);
            }
        };

        verifyAuth();
    }, [setUserRoles]);

    // Show loading state while verifying or leaving for Onboarding
    if (loading || redirectingToOnboarding) {
        return <ProtectedRouteLoadingSpinner />;
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        warn(
            'Unauthenticated access attempt - redirecting to login',
            {
                attemptedPath: location.pathname,
                search: location.search,
                timestamp: new Date().toISOString(),
            },
            'ProtectedRoute',
            'unauthenticated_redirect'
        );
        return <Navigate to='/login' replace />;
    }

    // Check for required roles
    if (requiredRoles.length > 0) {
        const hasRequiredRole = requiredRoles.some((role) => localUserRoles.includes(role));

        if (!hasRequiredRole) {
            warn(
                'Insufficient permissions - redirecting to dashboard',
                {
                    attemptedPath: location.pathname,
                    search: location.search,
                    requiredRoles: requiredRoles,
                    userRoles: localUserRoles,
                    timestamp: new Date().toISOString(),
                },
                'ProtectedRoute',
                'insufficient_permissions'
            );
            return <Navigate to='/dashboard' replace />;
        }

        // Log successful role-based access
        info(
            'Role-based access granted',
            {
                path: location.pathname,
                search: location.search,
                requiredRoles: requiredRoles,
                userRoles: localUserRoles,
                timestamp: new Date().toISOString(),
            },
            'ProtectedRoute',
            'role_access_granted'
        );
    }

    // Log successful navigation
    info(
        'Protected route access granted',
        {
            path: location.pathname,
            search: location.search,
            timestamp: new Date().toISOString(),
        },
        'ProtectedRoute',
        'navigation_success'
    );

    // If authenticated and has required roles, render the children
    return <Outlet />;
};

export default ProtectedRoute;
